import React from 'react';
import PropTypes from 'prop-types';
import {
  LuBriefcaseBusiness,
  LuBuilding2,
  LuCalendarDays,
  LuCheck,
  LuChevronRight,
  LuClock,
  LuEyeOff,
  LuFileSpreadsheet,
  LuFolderKanban,
  LuInfo,
} from 'react-icons/lu';
import Badge from '../../../../../components/ui/Badge';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { Abs, Btn, CARD, Dialog, DialogIcon, Field, Note, SectionHead } from '../kit';

export const MYPROJ = (top) => ({
  toggle: { x: 1040, y: top + 4, w: 216, h: 34 },
  card: (index) => ({ x: 256 + (index % 3) * 338, y: top + 70 + Math.floor(index / 3) * 146, w: 324, h: 132 }),
  cards: { x: 256, y: top + 70, w: 1000, h: 132 },
});

const CARDS = [
  { code: 'ONKO-301', name: 'p1', date: '12.01.2025', worker: 'primer', blind: 'blinded' },
  { code: 'KARDİYO-12', name: 'p2', date: '03.06.2024', worker: 'backup', blind: 'unblinded' },
  { code: 'NÖRO-7', name: 'p3', date: '20.09.2023', worker: 'primer', blind: 'blinded', passive: true },
];

/** Projelerim: one card per project with the employee's role badges. */
export const ProjectsPane = ({ top, state }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.projects.${key}`, vars);
  const M = MYPROJ(top);
  const cards = state.hidePassive ? CARDS.filter((card) => !card.passive) : CARDS;
  return (
    <>
      <SectionHead top={top} icon={LuBriefcaseBusiness} gradient="tw-from-indigo-400 tw-to-blue-600 tw-shadow-indigo-500/30" title={label('title')} description={label('description')} />
      <Btn rect={M.toggle} icon={LuEyeOff} pressed={state.togglePressed} className="tw-text-xs">{state.hidePassive ? label('showPassive', { count: 1 }) : label('hidePassive', { count: 1 })}</Btn>
      {cards.map((card, index) => (
        <Abs key={card.code} rect={M.card(index)} className={`tw-p-4 tw-transition-all tw-duration-300 ${card.passive ? 'tw-rounded-[1.5rem] tw-border-2 tw-border-dashed tw-border-slate-300 tw-bg-slate-50/70 dark:tw-border-white/15 dark:tw-bg-white/[0.02]' : CARD} ${state.pressed === index ? 'tw-scale-[0.98] tw-ring-2 tw-ring-teal-400/60' : ''}`}>
          <div className="tw-flex tw-items-start tw-gap-3">
            <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-indigo-500/10 tw-text-indigo-600 dark:tw-text-indigo-300"><LuFolderKanban aria-hidden="true" className="tw-h-4 tw-w-4" /></span>
            <span className="tw-min-w-0 tw-flex-1">
              <span className="tw-block tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{card.code}</span>
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-slate-500"><LuCalendarDays aria-hidden="true" className="tw-h-3 tw-w-3" />{card.date}</span>
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-truncate tw-text-[11px] tw-text-slate-500"><LuBuilding2 aria-hidden="true" className="tw-h-3 tw-w-3" />{t('userGuide.stage.center')}</span>
            </span>
            <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
          </div>
          <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-1.5">
            <Badge tone={card.worker === 'primer' ? 'info' : 'neutral'}>{label(card.worker)}</Badge>
            <Badge tone={card.blind === 'blinded' ? 'accent' : 'warning'}>{label(card.blind)}</Badge>
            {card.passive && <Badge tone="neutral">{label('passive')}</Badge>}
          </div>
          {card.passive && <p className="tw-mt-2 tw-text-[11px] tw-text-slate-500">{label('passiveRole')}</p>}
        </Abs>
      ))}
    </>
  );
};

ProjectsPane.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired };

export const VISITS = {
  dialog: { x: 120, y: 36, w: 1040, h: 648 },
  help: { x: 144, y: 120, w: 992, h: 44 },
  start: { x: 144, y: 202, w: 220, h: 40 },
  end: { x: 380, y: 202, w: 220, h: 40 },
  table: { x: 144, y: 260, w: 992, h: 300 },
  row: (index) => ({ x: 144, y: 298 + index * 52, w: 992, h: 52 }),
  detail: (index) => ({ x: 1044, y: 298 + index * 52 + 10, w: 80, h: 32 }),
  close: { x: 900, y: 632, w: 96, h: 36 },
  excel: { x: 1004, y: 632, w: 132, h: 36 },
};

const VISIT_ROWS = [
  { date: '02.10.2026', type: 'hastane', patient: 'P-1042', city: 'İstanbul', done: true },
  { date: '18.09.2026', type: 'home', patient: 'P-1088', city: 'İstanbul', done: true },
  { date: '09.10.2026', type: 'telefon', patient: 'P-1120', city: 'İstanbul', done: false },
  { date: '28.08.2026', type: 'hastane', patient: 'P-1042', city: 'İstanbul', done: true },
];

/** Proje Visitleri: the employee's own visits in a project (read-only). */
export const VisitsDialog = ({ appear, state }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.projectVisits.${key}`, vars);
  const rows = state.filtered ? VISIT_ROWS.slice(0, 3) : VISIT_ROWS;
  const columns = [['date', 140], ['type', 170], ['patient', 110], ['hospital', 220], ['city', 110], ['status', 130]];
  return (
    <Dialog rect={VISITS.dialog} appear={appear} footer title={label('title')} subtitle="ONKO-301" icon={<DialogIcon icon={LuFolderKanban} gradient="tw-from-indigo-400 tw-to-blue-600 tw-shadow-indigo-500/30" />}>
      <Note rect={VISITS.help} icon={LuInfo}>{label('help')}</Note>
      <Field rect={VISITS.start} label={label('startDate')} value={state.filtered ? '01.09.2026' : ''} placeholder="gg.aa.yyyy" focused={state.dateFocus === 'start'} />
      <Field rect={VISITS.end} label={label('endDate')} value={state.filtered ? '15.10.2026' : ''} placeholder="gg.aa.yyyy" focused={state.dateFocus === 'end'} />
      <span style={{ left: 620, top: 212 }} className="tw-absolute tw-flex tw-gap-2">
        <Badge tone="success">{label('completed', { count: rows.filter((row) => row.done).length })}</Badge>
        <Badge tone="warning">{label('ongoing', { count: rows.filter((row) => !row.done).length })}</Badge>
      </span>
      <Abs rect={{ ...VISITS.table, h: 38 }} className="tw-rounded-t-xl tw-bg-slate-50 dark:tw-bg-white/[0.04]" />
      {columns.reduce((acc, [key, w]) => {
        acc.items.push(<span key={key} style={{ left: acc.x, top: 272, width: w }} className="tw-absolute tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">{label(`columns.${key}`)}</span>);
        acc.x += w;
        return acc;
      }, { x: 160, items: [] }).items}
      <span style={{ left: 1044, top: 272 }} className="tw-absolute tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">{label('columns.action')}</span>
      {rows.map((row, index) => {
        const r = VISITS.row(index);
        return (
          <React.Fragment key={`${row.date}-${row.patient}`}>
            <Abs rect={r} className="tw-border-b tw-border-slate-100 dark:tw-border-white/5" />
            <span style={{ left: 160, top: r.y + 17 }} className="tw-absolute tw-text-sm tw-tabular-nums tw-text-slate-700 dark:tw-text-slate-200">{row.date}</span>
            <span style={{ left: 300, top: r.y + 17 }} className="tw-absolute tw-text-sm tw-text-slate-700 dark:tw-text-slate-200">{t(`profilePage.calendar.visitTypes.${row.type}`)}</span>
            <span style={{ left: 470, top: r.y + 17 }} className="tw-absolute tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{row.patient}</span>
            <span style={{ left: 580, top: r.y + 17, width: 210 }} className="tw-absolute tw-truncate tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{t('userGuide.stage.center')}</span>
            <span style={{ left: 800, top: r.y + 17 }} className="tw-absolute tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{row.city}</span>
            <span style={{ left: 910, top: r.y + 14 }} className="tw-absolute">
              <Badge tone={row.done ? 'success' : 'warning'}>
                {row.done ? <LuCheck aria-hidden="true" className="tw-h-3 tw-w-3" /> : <LuClock aria-hidden="true" className="tw-h-3 tw-w-3" />}
                {label(row.done ? 'status.done' : 'status.ongoing')}
              </Badge>
            </span>
            <Btn rect={VISITS.detail(index)} className={row.done ? '' : 'tw-opacity-40'} pressed={state.detailPressed && index === 0}>{label('detail')}</Btn>
          </React.Fragment>
        );
      })}
      <span style={{ left: 146, top: 642 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-500">{label('total', { count: rows.length })}</span>
      <Btn rect={VISITS.close} variant="ghost">{label('close')}</Btn>
      <Btn rect={VISITS.excel} variant="success" icon={LuFileSpreadsheet} pressed={state.excelPressed}>{label('excel')}</Btn>
    </Dialog>
  );
};

VisitsDialog.propTypes = { appear: PropTypes.number, state: PropTypes.object.isRequired };
